// TOP（Documentation/ui-concepts/top-2026-10-15 の案A）：境内の背景に出撃キャラの立ち絵を大きく、知らせは横長の札で積まずに（受付中のアンケートだけ右の列の小さなバナー）
//  左の列＝任務・プレゼント・お知らせ・物語（丸いアイコン＋赤い数字）、右の列＝共同国盗り・おすすめの吹き出し（新スキル／霊玉で式神を強化）・ショップ・おまもりガチャ、
//  下＝里の宝箱（たまったら開いて光る・押して受け取り）｜戦闘に戻る（中断中だけ）・出撃キャラの切替｜お供、その下に出撃・オート周回。
// キャラ・強化・里等の導線は左ナビ（PC）／下タブ（スマホ）にあるので、ここには並べない（物語・おまもりガチャは PC の左ナビにあるので、スマホだけ）。
import { canSuggestInstall, installApp, onInstallChange } from '../ui/pwa';
import { sfx } from '../audio';
import { CHARS, CHAR_IDS, SHIKIGAMI, stageKey, linePointsGain, monthlyEndingDays, shikiUpgradeHint, skillLines, stageLabel, unspentLinePoints, villagePending, type CharId } from '@hyakki/core';
import { api } from '../api';
import { faceStyle, hasFailed, isLoaded, loadSheet, portraitHtml, portraitKey } from '../assets';
import { LivePortrait, livePortrait } from '../ui/live-portrait';
import { levelInfo, stageById } from '../core-bridge';
import { avatarHtml, myAvatar, openAvatarPicker } from '../ui/avatar';
import { go, register } from '../router';
import { act, state, subscribe } from '../state';
import { hideTopNudge, openLinkDialog, showTopNudge } from '../ui/google-link';
import { autoRun, lastAutoRun } from '../autorun';
import { esc, fmt, jstDay, onAct, rewardHtml, wrapLabel } from '../ui/dom';
import { artIcon, icon } from '../ui/icons';
import { openModal, toast } from '../ui/overlay';
import { errorText } from '../ui/status';
import { setLeader } from '../ui/leader';
import { bindCompanionActions, companionChipHtml } from '../ui/companion';
import { seenLevel, unseenUnlocks } from '../ui/skill-news';

let missionsReady = 0; // 受け取れる任務の数（TOPを開いたときに取る）

/** 月パスが3日以内に終わるとき、TOP を開いたら1日1回だけ知らせる（自動では更新しないので） */
const PASS_NOTE_KEY = 'hyakki.passEndingNote';
function notePassEnding() {
  const days = state.profile && monthlyEndingDays(state.profile, Date.now());
  if (!days) return;
  try {
    if (localStorage.getItem(PASS_NOTE_KEY) === jstDay()) return;
    localStorage.setItem(PASS_NOTE_KEY, jstDay());
  } catch {
    // 保存できなくても知らせは出す
  }
  toast(`月パスはあと${days}日で終わります`, 'info', 4000);
}

/** 左上の名札：自分のアイコン（押すと変える）・名前・主Lv・経験値 */
/** ゲストの帯で流す文（2つ並べて切れ目なく流す） */
const LINK_MSG = 'ゲストで遊べるのは第1章クリアまでです。データはこの端末のブラウザにだけ保存されています。Google連携すると、第2章からの続きも、機種変更やほかの端末でも遊べます。';
/** 流れる1周の秒数（top.css の tl-marquee と同じ）。描き直しても続きから流れるよう、時刻から位置を決める */
const LINK_LOOP_SEC = 22;

function mePlateHtml() {
  const me = state.me;
  if (!me) return '';
  const lv = levelInfo(state.resources.exp ?? 0, state.profile) ?? me.level;
  const pct = lv?.need ? Math.min(100, (lv.into / lv.need) * 100) : 100;
  return `<button class="oshi-plate plate me-plate" data-act="avatar" aria-label="${esc(me.user.displayName)}・主Lv${lv?.level ?? ''}（姿を変える）" title="あなたの姿を変える">
    ${avatarHtml(myAvatar(), 38, 'mp-av')}
    <span class="mp-txt"><b class="mp-name">${esc(me.user.displayName)}</b><span class="mp-lv">主Lv.<b>${lv?.level ?? '—'}</b></span>
      <i class="mp-exp" role="progressbar" aria-label="経験値" aria-valuemin="0" aria-valuemax="100" aria-valuenow="${Math.round(pct)}"><i style="width:${pct}%"></i></i></span></button>`;
}

register('top', {
  title: 'TOP',
  mount(el) {
    let alive = true;
    const draw = () => {
      if (!alive) return; // 非同期の読み込みが画面を離れたあとに終わったとき
      const oshi = state.oshi;
      const c = CHARS[oshi];
      el.innerHTML = `
        <div class="top ${showTopNudge() || kishinBuff() ? 'has-link' : ''}">
          <div class="top-bg" aria-hidden="true"></div>
          ${!showTopNudge() ? kishinBandHtml() : ''}
          ${showTopNudge() ? `<div class="top-link" role="note"><span class="tl-msg" style="--tl-at:-${((Date.now() / 1000) % LINK_LOOP_SEC).toFixed(2)}s"><span>${LINK_MSG}</span><span aria-hidden="true">${LINK_MSG}</span></span><button class="btn small primary" data-act="link">Google連携</button><button class="icon-btn tl-x" data-act="linkhide" aria-label="閉じる">×</button></div>` : ''}
          <div class="oshi">
            <div class="oshi-figure">${portraitHtml(oshi, 'oshi-img')}</div>
          </div>
          ${mePlateHtml()}
          <div class="top-col l">
            <nav class="rail" aria-label="お知らせ・受け取り">
              ${railBtn('mission', '任務', 'go" data-to="missions', missionsReady)}
              ${railBtn('present', 'プレゼント', 'go" data-to="presents', state.me?.unreadPresents ?? 0)}
              ${railBtn('bell', 'お知らせ', 'go" data-to="news', 0)}
              ${railBtn('story', '物語', 'go" data-to="stories', 0, 'sp-only')}
            </nav>
          </div>
          <div class="top-col r">
            ${tipHtml()}
            <nav class="rail" aria-label="ショップ・おまもりガチャ">
              ${railBtn('shop', 'ショップ', 'go" data-to="shop', 0)}
              ${railBtn('gacha', 'おまもりガチャ', 'go" data-to="gacha', 0, 'sp-only')}
            </nav>
            ${bannerHtml()}
          </div>
          <div class="top-dock">
            <div class="dock-l">${chestHtml()}</div>
            <div class="dock-m">
            ${resumeHtml()}
            <div class="oshi-pick" role="radiogroup" aria-label="出撃キャラ">
              ${CHAR_IDS.map(
                (id) => `<button class="op ${id === oshi ? 'on' : ''}" role="radio" aria-checked="${id === oshi}" data-act="oshi" data-id="${id}">
                  <span class="op-face" style="${faceStyle(id)}"></span><span class="op-name">${CHARS[id].name}</span></button>`,
              ).join('')}
            </div>
            </div>
            <div class="dock-r top-companion">${companionChipHtml()}</div>
            <div class="top-sortie">
              <button class="btn primary huge sortie" data-act="sortie" aria-label="${c.name}で出撃"><span class="sortie-l">出撃</span></button>
              ${autoHtml()}
            </div>
          </div>
        </div>`;
      mountLive();
    };
    // 動く立ち絵（素材のあるキャラだけ）：読み込めたら静止画と入れ替える。描き直しでは canvas を付け替えるだけ
    const mountLive = () => {
      const id = state.oshi;
      if (!LivePortrait.available(id)) return;
      void livePortrait(id).then((lp) => {
        const host = el.querySelector('.oshi-figure');
        if (!alive || !lp || !host || state.oshi !== id) return;
        lp.attach(host, CHARS[id].name);
      });
    };
    draw();
    notePassEnding();
    // 出撃キャラの立ち絵：まだなら読んでから描き直す（無ければちびキャラの拡大）
    const wantPortrait = () => {
      const key = portraitKey(state.oshi);
      if (!isLoaded(key) && !hasFailed(key)) void loadSheet(key).then(() => draw());
    };
    wantPortrait();
    void api
      .missions()
      .then((r) => {
        const all = [...(r.daily ?? r.missions ?? []), ...(r.weekly ?? [])];
        missionsReady = all.filter((m) => m.progress >= m.target && !m.claimed).length;
        draw();
      })
      .catch(() => {});
    const timer = setInterval(draw, 60_000); // 里の蓄積
    // 寄進の帯の残り時間（切れたら帯ごと描き直す）
    const kishinTimer = setInterval(() => {
      const left = el.querySelector<HTMLElement>('[data-kleft]');
      if (!left) return;
      const kb = kishinBuff();
      if (!kb) return draw();
      left.textContent = kishinLeft(kb.until);
    }, 1000);
    const off = subscribe((ch) => {
      if (ch.has('oshi')) wantPortrait();
      if (ch.has('oshi') || ch.has('progress') || ch.has('suspended') || ch.has('me') || ch.has('profile') || ch.has('resources')) draw();
    });
    // ボタン1つで入れられるようになった・入れた（ブラウザの知らせ）
    const offInstall = onInstallChange(() => draw());
    const offAct = onAct(el, {
      sortie: () => go('stages'),
      auto: () => (autoRun()?.parked && state.suspended ? go('battle', { resume: 1, autorun: 1 }) : go('stages', { auto: 1 })),
      resume: () => go('battle', autoRun()?.parked ? { resume: 1, autorun: 1 } : { resume: 1 }),
      kishin: () => go('shop', { cat: 'item', focus: 'kishin', from: 'kishin_band' }),
      offer: (b) => go('shop', { cat: 'limited', focus: b.dataset.id!, from: 'top_offer' }),
      offerhide: (b) => {
        hideOfferToday(b.dataset.id!);
        draw();
      },
      go: (b) => go(b.dataset.to!, b.dataset.tab ? { tab: b.dataset.tab, ...(b.dataset.shiki ? { shiki: b.dataset.shiki } : {}), ...(b.dataset.c ? { c: b.dataset.c } : {}) } : {}),
      skills: () => go('skills'),
      install: () => void installApp(),
      installhide: () => {
        try {
          localStorage.setItem(INSTALL_HIDE, String(Date.now() + 14 * 86_400_000));
        } catch {
          /* 覚えられなくても消す */
        }
        draw();
      },
      oshi: (b) => void setLeader(b.dataset.id as CharId),
      avatar: () => openAvatarPicker(),
      link: () => openLinkDialog('top'),
      linkhide: () => {
        hideTopNudge();
        draw();
      },
      collect: async (b) => {
        (b as HTMLButtonElement).disabled = true;
        try {
          const r = await act(api.collectVillage());
          sfx('coin');
          openModal(rewardHtml(r.gain ?? r), { title: '受け取りました' });
        } catch (e) {
          toast(errorText(e), 'error');
          (b as HTMLButtonElement).disabled = false;
        }
      },
    });
    const offComp = bindCompanionActions(el, draw);
    return () => {
      alive = false;
      clearInterval(timer);
      clearInterval(kishinTimer);
      off();
      offAct();
      offComp();
      offInstall();
    };
  },
});

/** 左右の列の丸いアイコン（右上に赤い数字） */
function railBtn(art: string, label: string, actAttrs: string, badge: number, cls = ''): string {
  return `<button class="rail-btn ${cls}" data-act="${actAttrs}" aria-label="${label}${badge ? `（${badge}件）` : ''}"><span class="rb-ic">${topIcon(art)}</span><span class="rb-l">${wrapLabel(label)}</span>${badge ? `<em class="rb-n">${badge > 99 ? '99+' : badge}</em>` : ''}</button>`;
}
/** TOP 用の絵のアイコン（ナビの絵と、TOP だけの絵 art/ui/top-icons.webp） */
const TOP_ART: Record<string, number> = { story: 0, chest: 1, chestOpen: 2, auto: 3 };
function topIcon(name: string): string {
  const i = TOP_ART[name];
  if (i === undefined) return artIcon(name);
  return `<i class="ic ic-top" style="background-position:${(i % 2) * 100}% ${Math.floor(i / 2) * 100}%" aria-hidden="true"></i>`;
}

/** 里の宝箱：たまっていれば開いて光り、押すと受け取る。無ければ閉じた箱（押すと里へ） */
function chestHtml(): string {
  const p = state.profile;
  if (!p) return '';
  const pend = villagePending(p, Date.now());
  const has = pend.zeni + pend.reidama + pend.zaimoku + pend.exp > 0;
  if (!has) return `<button class="chest" data-act="go" data-to="village" aria-label="里">${topIcon('chest')}<span class="ch-l"><small>里の収益</small><b>たまり中</b></span></button>`;
  const main = pend.zeni
    ? `<i class="ri ri-zeni"></i>${fmt(pend.zeni)}`
    : pend.zaimoku
      ? `<i class="ri ri-zaimoku"></i>${fmt(pend.zaimoku)}`
      : `<i class="ri ri-reidama"></i>${fmt(pend.reidama)}`;
  return `<button class="chest full" data-act="collect" aria-label="里の収益を受け取る">${topIcon('chestOpen')}<span class="ch-l"><small>里の収益</small><b class="res-num">${main}</b></span><span class="ch-get">受取</span></button>`;
}

/** おすすめ（吹き出し1つ）：スキルポイント ＞ 新しいスキル ＞ 霊玉で式神を強化 */
function tipHtml(): string {
  const p = state.profile;
  if (!p) return '';
  // 系統のキャラ：振っていないポイントがある（手動）か、スキルを見てからポイントが増えた
  const lineChar = CHAR_IDS.find((c) => skillLines(c));
  if (lineChar) {
    const un = unspentLinePoints(p)[0];
    const gain = linePointsGain(p, seenLevel(p.level), p.level);
    if (un || gain > 0)
      return `<button class="top-tip" data-act="go" data-to="chara" data-tab="skill" data-c="${un?.charId ?? lineChar}"><em>スキル</em>${icon('sp')}<span>ポイント${un ? `残り${un.left}` : `+${gain}`}</span></button>`;
  }
  const fresh = unseenUnlocks(p.level);
  if (fresh.length) return `<button class="top-tip" data-act="skills"><em>新スキル</em>${icon('sp')}<span>スキルを見る</span></button>`;
  // 霊玉で式神Lv を上げられる（1段だけなら出さない。毎日の霊玉で常に出続けないように。BALANCE.md 17章）
  const hint = shikiUpgradeHint(p, state.resources.reidama ?? 0);
  if (hint && hint.to - hint.level >= 2)
    return `<button class="top-tip" data-act="go" data-to="enhance" data-tab="shiki" data-shiki="${esc(hint.enemyId)}" title="${esc(SHIKIGAMI[hint.enemyId]?.name ?? '')} 式神Lv${hint.level}→${hint.to}（霊玉${fmt(hint.cost)}）"><i class="ri ri-reidama"></i><span>霊玉で強化</span></button>`;
  return '';
}

/** 今効いている寄進（全員の主EXP+20%）。無ければ null */
function kishinBuff() {
  const now = Date.now();
  return state.me?.buffs?.find((b) => b.kind === 'exp' && b.until > now) ?? null;
}
const kishinLeft = (until: number) => {
  const s = Math.max(0, Math.ceil((until - Date.now()) / 1000));
  return `あと${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;
};
/** 寄進の帯（寄進が効いている間だけ、上の帯の場所。ゲストの連携の帯が出ているときは出さない）：誰の寄進か・主EXP+20%・残り時間、押すとショップの寄進へ */
function kishinBandHtml(): string {
  const kb = kishinBuff();
  if (!kb) return '';
  const pct = Math.round(kb.mult * 100);
  return `<div class="top-link kishin" role="status"><span class="tk-msg">${icon('cracker')}<b>${esc(kb.donor ?? '名無し')}</b>さんの寄進<em>主EXP+${pct}%</em><i data-kleft>${kishinLeft(kb.until)}</i></span><button class="btn small primary" data-act="kishin">寄進する</button></div>`;
}

/** 右の列のバナー：受付中で未回答のアンケート（1章のボスを倒してから。答えたら消える）、スマホのブラウザならホーム画面に追加 */
function bannerHtml(): string {
  return offerBannerHtml() + surveyBannerHtml() + installBannerHtml();
}

/** 限定の商品のバナー（1つだけ。討伐記念10連 ＞ リリース記念セット ＞ はじめての10連。1-1 を勝ってから）。押すとショップの「限定」へ、「×」でその日は出さない */
const OFFER_HIDE = 'hk.offerHide';
const OFFER_LABEL: Record<string, string> = { gacha10_boss: '討伐記念<br>10連', release_set: 'リリース<br>記念', gacha10_first: 'はじめての<br>10連' };
function offerHidden(): Record<string, string> {
  try {
    return JSON.parse(localStorage.getItem(OFFER_HIDE) ?? '{}');
  } catch {
    return {};
  }
}
function hideOfferToday(id: string) {
  try {
    localStorage.setItem(OFFER_HIDE, JSON.stringify({ ...offerHidden(), [id]: state.me?.day ?? '' }));
  } catch {
    /* 覚えられなくても閉じたまま描き直す */
  }
}
function offerBannerHtml(): string {
  if (!((state.profile?.progress[stageKey(1)]?.clears ?? 0) > 0)) return '';
  const now = Date.now();
  const hidden = offerHidden();
  const offers = state.me?.offers ?? [];
  const o = Object.keys(OFFER_LABEL)
    .map((id) => offers.find((x) => x.id === id))
    .find((x) => x && (!x.until || x.until > now) && hidden[x.id] !== state.me?.day);
  if (!o) return '';
  return `<div class="top-banner-wrap"><button class="top-banner offer" data-act="offer" data-id="${o.id}" aria-label="ショップの限定"><em>${offerLeftText(o.until, now)}</em><img src="/art/ui/offer-card.webp" alt="" width="44" height="44"><b>${OFFER_LABEL[o.id]}</b></button><button class="top-banner-x" data-act="offerhide" data-id="${o.id}" aria-label="今日は出さない">${icon('close')}</button></div>`;
}
/** 締め切りの札：1回だけ／あと○分・○時間（2日未満）／M/Dまで */
function offerLeftText(until: number | null, now: number) {
  if (!until) return '1回だけ';
  const left = until - now;
  if (left < 3_600_000) return `あと${Math.max(1, Math.ceil(left / 60_000))}分`;
  if (left < 48 * 3_600_000) return `あと${Math.floor(left / 3_600_000)}時間`;
  const d = new Date(until - 1);
  return `${d.getMonth() + 1}/${d.getDate()}まで`;
}

/** ホーム画面に追加（アプリとして開いていないスマホ。1-1 を勝ってから。「×」で14日出さない） */
const INSTALL_HIDE = 'hk.pwaHideUntil';
function installBannerHtml(): string {
  if (!canSuggestInstall() || !((state.profile?.progress[stageKey(1)]?.clears ?? 0) > 0)) return '';
  try {
    if (Number(localStorage.getItem(INSTALL_HIDE) ?? 0) > Date.now()) return '';
  } catch {
    /* 覚えられなくても出す */
  }
  return `<div class="top-banner-wrap"><button class="top-banner install" data-act="install" aria-label="ホーム画面に追加"><em>おすすめ</em><img src="/icons/icon-192.png" alt="" width="34" height="34"><b>アプリに<br>する</b></button><button class="top-banner-x" data-act="installhide" aria-label="しばらく出さない">${icon('close')}</button></div>`;
}

function surveyBannerHtml(): string {
  const sv = state.me?.survey;
  if (!sv || sv.answered || !((state.profile?.progress[stageKey(10)]?.clears ?? 0) > 0)) return '';
  const r = sv.reward[0];
  return `<button class="top-banner" data-act="go" data-to="survey" aria-label="アンケートに答える"><em>受付中</em>${icon('feather')}<b>アンケート</b>${r && r.kind !== 'item' ? `<small class="res-num"><i class="ri ri-${r.kind}"></i>${fmt(r.amount)}</small>` : ''}</button>`;
}

/** 中断中の戦闘：立ち絵の足元（出撃キャラの切替の上）に */
function resumeHtml(): string {
  if (!state.suspended) return '';
  const st = stageById(state.suspended.stageId);
  return `<button class="top-resume" data-act="resume">${artIcon('sortie')}<span><b>戦闘に戻る</b><small>${esc(st ? stageLabel(st.id, { short: true }) : '')}・${CHARS[state.suspended.charId].name}</small></span></button>`;
}

/** オート周回：出撃の右の丸いボタン（状態を下に小さく） */
function autoHtml(): string {
  if (state.me?.user.guest) return '';
  const run = lastAutoRun();
  const info = run ? (run.endedAt ? '停止' : `${run.battles}戦`) : '';
  return `<button class="top-auto ${run && !run.endedAt ? 'on' : ''}" data-act="auto" aria-label="オート周回${info ? `（${esc(info)}）` : ''}">${topIcon('auto')}<b>オート</b>${info ? `<small>${esc(info)}</small>` : ''}</button>`;
}

